/**
 * MeetingPrepSettings Component
 * Settings modal for Meeting Prep column
 */

import { useState, useEffect } from 'react';
import { X, Folder, Sparkles, Check } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface MeetingPrepSettingsData {
  aiMatchingEnabled: boolean;
  selectedFolders: string[];
}

interface MeetingPrepSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  availableFolders?: Array<{ id: string; name: string; displayName: string }>;
}

const SETTINGS_STORAGE_KEY = 'kolumi:meetingPrep:settings';

export function getStoredSettings(accountId: string): MeetingPrepSettingsData {
  try {
    const stored = localStorage.getItem(`${SETTINGS_STORAGE_KEY}:${accountId}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load meeting prep settings:', e);
  }
  return {
    aiMatchingEnabled: true,
    selectedFolders: [],
  };
}

function saveSettings(accountId: string, settings: MeetingPrepSettingsData) {
  try {
    localStorage.setItem(`${SETTINGS_STORAGE_KEY}:${accountId}`, JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save meeting prep settings:', e);
  }
}

export default function MeetingPrepSettings({
  isOpen,
  onClose,
  accountId,
  availableFolders = [],
}: MeetingPrepSettingsProps) {
  const [settings, setSettings] = useState<MeetingPrepSettingsData>(() =>
    getStoredSettings(accountId)
  );

  useEffect(() => {
    if (isOpen) {
      setSettings(getStoredSettings(accountId));
    }
  }, [isOpen, accountId]);

  const handleSave = () => {
    saveSettings(accountId, settings);
    onClose();
  };

  const toggleFolder = (folderId: string) => {
    setSettings((prev) => {
      const folders = prev.selectedFolders.includes(folderId)
        ? prev.selectedFolders.filter((id) => id !== folderId)
        : [...prev.selectedFolders, folderId];
      return { ...prev, selectedFolders: folders };
    });
  };

  const toggleAiMatching = () => {
    setSettings((prev) => ({
      ...prev,
      aiMatchingEnabled: !prev.aiMatchingEnabled,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[var(--bg-primary)] rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden border border-[var(--border-default)]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-default)]">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            Meeting Prep Einstellungen
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* AI Matching Toggle */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[var(--brand-primary)]" />
                <span className="font-medium text-[var(--text-primary)]">
                  AI Matching
                </span>
              </div>
              <button
                onClick={toggleAiMatching}
                className={cn(
                  'relative w-11 h-6 rounded-full transition-colors',
                  settings.aiMatchingEnabled
                    ? 'bg-[var(--brand-primary)]'
                    : 'bg-[var(--bg-tertiary)]'
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                    settings.aiMatchingEnabled && 'translate-x-5'
                  )}
                />
              </button>
            </div>
            <p className="text-sm text-[var(--text-tertiary)]">
              Verwendet KI um relevante Mails automatisch zu erkennen (demnächst verfügbar)
            </p>
          </div>

          {/* Folder Selection */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Folder className="w-4 h-4 text-[var(--brand-primary)]" />
              <span className="font-medium text-[var(--text-primary)]">
                Ordner durchsuchen
              </span>
            </div>
            <p className="text-sm text-[var(--text-tertiary)]">
              Wähle die Ordner, in denen nach verwandten Mails gesucht werden soll.
              Keine Auswahl = Alle Ordner.
            </p>

            <div className="max-h-48 overflow-y-auto border border-[var(--border-default)] rounded-lg">
              {availableFolders.length === 0 ? (
                <div className="p-4 text-center text-[var(--text-tertiary)] text-sm">
                  Keine Ordner verfügbar
                </div>
              ) : (
                <div className="divide-y divide-[var(--border-default)]">
                  {availableFolders.map((folder) => {
                    const isSelected = settings.selectedFolders.includes(folder.id);
                    return (
                      <button
                        key={folder.id}
                        onClick={() => toggleFolder(folder.id)}
                        className={cn(
                          'w-full flex items-center justify-between p-3 text-left transition-colors',
                          isSelected
                            ? 'bg-[var(--brand-primary)]/10'
                            : 'hover:bg-[var(--bg-hover)]'
                        )}
                      >
                        <span
                          className={cn(
                            'text-sm',
                            isSelected
                              ? 'text-[var(--brand-primary)] font-medium'
                              : 'text-[var(--text-secondary)]'
                          )}
                        >
                          {folder.displayName || folder.name}
                        </span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-[var(--brand-primary)]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-[var(--border-default)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] rounded-lg transition-colors"
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
